import React from 'react';
import styled from 'styled-components';
import { FaGraduationCap, FaBriefcase, FaCogs, FaHeart, FaMapMarkerAlt } from 'react-icons/fa';
import { aboutInfo, personalInfo } from '../data/personal';
import { Section, Card } from '../components/common';

const AboutContainer = styled(Section)`
  background: linear-gradient(180deg, ${({ theme }: { theme: any }) => theme.colors.background} 0%, ${({ theme }: { theme: any }) => theme.colors.surface} 50%, ${({ theme }: { theme: any }) => theme.colors.background} 100%);
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }: { theme: any }) => theme.spacing['4xl']};
  align-items: center;

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }: { theme: any }) => theme.spacing['3xl']};
    text-align: center;
  }
`;

const AboutContent = styled.div`
  .section-title {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes['4xl']};
    font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.bold};
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.lg};
    background: linear-gradient(135deg, ${({ theme }: { theme: any }) => theme.colors.navyBlue}, ${({ theme }: { theme: any }) => theme.colors.lightNavy});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }: { theme: any }) => theme.fontSizes['3xl']};
    }
  }

  .intro {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
    color: ${({ theme }: { theme: any }) => theme.colors.textSecondary};
    line-height: 1.8;
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing['2xl']};
  }

  .highlight {
    color: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
    font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.semibold};
  }
`;

const PersonalInfo = styled.div`
  margin-top: ${({ theme }) => theme.spacing['2xl']};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};

  .personal-title {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};

    .icon {
      color: ${({ theme }) => theme.colors.navyBlue};
    }
  }

  .personal-content {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.7;
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const InfoCards = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(30, 58, 138, 0.2);
    border-color: ${({ theme }) => theme.colors.navyBlue};
  }
  
  .icon {
    color: ${({ theme }) => theme.colors.navyBlue};
    font-size: ${({ theme }) => theme.fontSizes.xl};
    margin-top: ${({ theme }) => theme.spacing.xs};
    flex-shrink: 0;
  }

  .content {
    flex: 1;
    
    .title {
      font-size: ${({ theme }) => theme.fontSizes.lg};
      font-weight: ${({ theme }) => theme.fontWeights.semibold};
      color: ${({ theme }) => theme.colors.textPrimary};
      margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
    
    .description {
      color: ${({ theme }) => theme.colors.textSecondary};
      font-size: ${({ theme }) => theme.fontSizes.base};
      line-height: 1.6;
    }
  }
`;

export const AboutSection: React.FC = () => {
  return (
    <AboutContainer id="about">
        <AboutGrid>
          <AboutContent className="fade-in-up">
            <header>
              <h2 className="section-title">About Me</h2>
            </header>
            <p className="intro">
              I'm a <span className="highlight">Senior Software Engineer</span> with a passion for building 
              scalable backend systems and crafting efficient APIs. Based in <span className="highlight">Kochi, Kerala, India</span>, 
              I specialize in backend architecture, database optimizations, and creating robust solutions 
              that power modern applications.
            </p>
            <p className="intro">
              When I'm not coding, you'll find me immersed in a good book, constantly learning and exploring 
              new technologies. Currently diving deep into <span className="highlight">AI and machine learning</span> 
              to expand my technical horizons.
            </p>

            <PersonalInfo>
              <div className="personal-title">
                {React.createElement(FaHeart as any, { 
                  className: "icon",
                  'aria-hidden': true 
                })}
                Beyond the Code
              </div>
              <p className="personal-content">
                {aboutInfo.personalContent}
              </p>
            </PersonalInfo>
          </AboutContent>

          <section className="fade-in-up" aria-labelledby="professional-info-heading">
            <h3 id="professional-info-heading" className="sr-only">Professional Information</h3>
            <InfoCards role="list" aria-label="Professional background and expertise">
              <InfoCard>
                {React.createElement(FaGraduationCap as any, { 
                  className: "icon",
                  'aria-hidden': true 
                })}
                <div className="content">
                  <div className="title">Education</div>
                  <div className="description">
                    {aboutInfo.education}<br/>
                    Strong foundation in software engineering principles and computer science fundamentals
                  </div>
                </div>
              </InfoCard>

              <InfoCard>
                {React.createElement(FaBriefcase as any, { 
                  className: "icon",
                  'aria-hidden': true 
                })}
                <div className="content">
                  <div className="title">Current Role</div>
                  <div className="description">
                    {aboutInfo.role}<br/>
                    Focusing on backend architecture, API development, and system optimization
                  </div>
                </div>
              </InfoCard>

              <InfoCard>
                {React.createElement(FaCogs as any, { 
                  className: "icon",
                  'aria-hidden': true 
                })}
                <div className="content">
                  <div className="title">Specializations</div>
                  <div className="description">
                    Backend Architecture • API Design • Database Optimization<br/>
                    Building scalable and efficient server-side solutions
                  </div>
                </div>
              </InfoCard>

              <InfoCard>
                {React.createElement(FaMapMarkerAlt as any, { 
                  className: "icon",
                  'aria-hidden': true 
                })}
                <div className="content">
                  <div className="title">Location & Availability</div>
                  <div className="description">
                    {personalInfo.location}<br/>
                    Open for networking and passionate about new opportunities
                  </div>
                </div>
              </InfoCard>
            </InfoCards>
          </section>
        </AboutGrid>
    </AboutContainer>
  );
}; 